import type { StateClassKey } from "@tdelivr/design-tokens";

/*
 * **Copied from `apps/tenant-admin-console/src/board.ts` for ONE screen, and the arithmetic below is
 * that console's rather than this one's.** The original's case is "the next five screens are the same
 * screen five times"; this console draws one board, so by that argument it would write the board
 * inline.
 *
 * It is a copy anyway, for a different reason: what this module and `ui/RecordsBoard.tsx` carry
 * between them is the whole of `CLAUDE.md` design rule 4 — search, filter, sort, pagination with
 * rows-per-page, export to CSV, Excel and PDF, and an empty state with a Clear filters button — plus
 * the row detail panel section 1 names among the things not to remove. Writing that inline for one
 * screen is ~500 lines this console would then own a second opinion about, on a console whose
 * remaining fifteen pages are all the same template waiting for a listing. The first of those to be
 * served costs nothing here.
 *
 * ADR 0021 is why it is a copy rather than a package. Nothing below has been changed from the
 * original except this note, so a fix there is a diff away from applying here.
 */

/**
 * What a board of rows is, away from any one service — and the searching, ordering and paging the
 * design's own `gridUI()` does to it.
 *
 * **This module exists because the next five screens are the same screen five times.**
 * `17 WP16 Tenant Admin.dc.html` draws every one of its fifteen pages from one `PAGES` table and
 * one `gridUI()`: a title, a source chip, a sentence, three data columns, a state badge, a closing
 * note. The four screens built before these five each wrote that template out again, and the four
 * copies are ~500 lines apiece with search, sort, paging and three export formats restated in each.
 * A fifth, sixth, seventh, eighth and ninth copy would be two and a half thousand lines of the same
 * code, and the cost is not tidiness: the `--cross` tint, the pager's bounds and the export's
 * column set are decisions that have to agree across the console, and five copies is five places
 * for them to stop agreeing.
 *
 * So the board is written once, here and in `ui/RecordsBoard.tsx`, and each page supplies what is
 * its own: the operation it reads, the cells it makes of a row, the state it decides, the filter it
 * offers, and the words. The four existing screens are deliberately **not** rewritten onto it —
 * each is pinned by a committed fidelity check, and rewriting a measured screen to share code with
 * an unmeasured one is how a green check stops meaning anything.
 *
 * Nothing here talks to a service and nothing here decides a colour. The design's own `gridUI()` is
 * the specification for every behaviour below, including the parts that look arbitrary:
 *
 *   - search matches the row's **texts**, which on that file is every string the row renders
 *     including its state word, so a search for `live` finds rows by their badge;
 *   - `default` is the order the service answered in, never re-sorted;
 *   - `az`/`za` sort on the row's **name**, which is its first cell;
 *   - the page is clamped into range rather than emptied, so narrowing a filter while on page 4
 *     shows rows instead of nothing.
 */

/** One cell of one row, with the two things a cell can be beyond its text. */
export interface BoardCell {
  readonly text: string;
  /**
   * Set in Geist Mono, which the design does for a value matching its own prefix list — `ten_`,
   * `evt_`, `RPL`, `RSK`, `wf_`, `/v1`, `key`, `offset` — and which this console extends to a
   * published identifier a page is drawing in place of a name. Q-102 is the one case: a rate card's
   * `productId` where the design writes a product's name.
   */
  readonly mono?: boolean;
  /**
   * A designed column this operation cannot fill, drawn as the design's own `—`.
   *
   * The design writes `—` itself, on the policies row whose value is not set, so this is its
   * vocabulary for nothing rather than one invented here. Drawn in `--text3` and carrying the
   * reason on its `title`, because the alternatives are both worse: an invented value is the thing
   * CLAUDE.md section 2 forbids, and a dropped column silently changes the grid's shape.
   */
  readonly absent?: boolean;
  /** Why the cell says what it says, where a screen has a reason and the design has no room for it. */
  readonly title?: string;
}

/** What the badge at the end of a row says. */
export interface BoardState {
  readonly classKey: StateClassKey;
  readonly label: string;
  readonly title?: string;
}

/** One line of the design's detail panel: a field its own contract publishes, and its value. */
export interface DetailField {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
  readonly absent?: boolean;
}

/** One row of a board, and everything the design draws about it. */
export interface BoardRow {
  /** React's key and the export's stable identity: the row's own published identifier. */
  readonly id: string;
  readonly cells: readonly BoardCell[];
  readonly state: BoardState;
  /**
   * The design's tinted row, which it reserves for its `human` and `failed` classes.
   *
   * Never the only signal: the badge beside it carries a glyph and a word.
   */
  readonly tinted?: boolean;
  /** The design's detail panel, beyond the columns it repeats from the grid. */
  readonly detail: readonly DetailField[];
  /** The design's `History` block: created, last changed, version. */
  readonly history: readonly DetailField[];
  /** The glyph in the detail panel's head, which the design takes from the page's own form icon. */
  readonly glyph: string;
}

/** The sort options the design's toolbar offers, in its order and its words. */
export type BoardOrder = "default" | "state" | "az" | "za";

export const ORDER_CHOICES: readonly { readonly value: BoardOrder; readonly label: string }[] = [
  { value: "default", label: "Default order" },
  { value: "state", label: "State" },
  { value: "az", label: "Name A–Z" },
  { value: "za", label: "Name Z–A" },
];

/** The design's own rows-per-page choices, in its order. */
export const PER_PAGE_CHOICES = [5, 10, 25, 50] as const;

/** The export formats the design's menu offers, in its order and its words. */
export const EXPORTS = [
  { format: "csv", glyph: "description", label: "CSV" },
  { format: "xls", glyph: "table_view", label: "Excel (.xls)" },
  { format: "pdf", glyph: "picture_as_pdf", label: "PDF" },
] as const;

export type ExportFormat = (typeof EXPORTS)[number]["format"];

export interface BoardQuery {
  readonly search: string;
  readonly order: BoardOrder;
  /** 1-based, as the pager shows it. */
  readonly page: number;
  readonly perPage: number;
}

export interface BoardPage {
  readonly rows: readonly BoardRow[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly BoardRow[];
  readonly total: number;
  readonly pageCount: number;
}

/**
 * Every word a search should match on one row.
 *
 * The design's `gridTexts` collects every string the row renders, its state word included, and
 * nothing else — so an identifier a cell does not draw is not searchable and a badge's word is.
 * The detail panel's fields are included here where the design's are not, and that is a deliberate
 * difference rather than a slip: a row whose only distinguishing value is in its detail panel —
 * a configuration entry's namespace, a customer's identity subject — would otherwise be
 * unfindable on a board whose first column is a key that repeats across namespaces.
 */
function searchableText(row: BoardRow): string {
  return [
    ...row.cells.filter((cell) => cell.absent !== true).map((cell) => cell.text),
    row.state.label,
    ...row.detail.filter((field) => field.absent !== true).map((field) => field.value),
  ]
    .join(" ")
    .toLowerCase();
}

/**
 * Filter, order and page what the screen holds.
 *
 * `default` is the order the service answered in and the screen must not quietly replace it. On
 * three of these five listings that order is load-bearing: PRC-01 answers rate cards newest first
 * so the version awaiting approval leads, CUS-01 answers customers newest first, and TEN-02 pages
 * configuration entries over `(namespace, key)` so a namespace's entries stay together.
 */
export function boardPage(rows: readonly BoardRow[], query: BoardQuery): BoardPage {
  const needle = query.search.trim().toLowerCase();
  const matched =
    needle.length === 0 ? [...rows] : rows.filter((row) => searchableText(row).includes(needle));

  const ordered =
    query.order === "default"
      ? matched
      : [...matched].sort((left, right) => {
          if (query.order === "state") {
            return left.state.label.localeCompare(right.state.label);
          }
          // "Name" is the first column, as the design's A–Z is: the row's own first cell.
          const compared = (left.cells[0]?.text ?? "").localeCompare(right.cells[0]?.text ?? "");
          return query.order === "az" ? compared : -compared;
        });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return { rows: ordered.slice(from, from + query.perPage), all: ordered, total, pageCount };
}

/**
 * The one sentence a board owes an administrator about what it fetched, or nothing.
 *
 * Only when it is true. All four of these listings publish a `nextAfter`, so a truncated answer can
 * be told from a complete one — which is what the brand board cannot do and why it has no count
 * anywhere on it. A board taking one page and showing it under a design that implies a total would
 * be the R-04 error; saying so is the whole of the fix.
 *
 * @param fetched how many rows came back, or `undefined` before anything has.
 * @param truncated whether the listing answered a cursor, meaning this is the head of the list.
 * @param noun what the rows are, in the plural, for the sentence.
 * @param ordering how the service ordered them, so the sentence says which head this is.
 */
export function boundedNote(
  fetched: number | undefined,
  truncated: boolean,
  noun: string,
  ordering: string,
): string | undefined {
  if (fetched === undefined || !truncated) return undefined;
  return `More than ${String(fetched)} ${noun} match. This board shows the first page, ${ordering}, so the count is a floor rather than a total.`;
}

/**
 * The month abbreviations the design writes, read out of the platform's own locale data.
 *
 * **Three letters, always, and `Intl` alone does not give that.** The design's dates are
 * `1 Sep 2026`, `15 Aug 2026`, `1 Jan 2027` and `Mar 2024`: three letters in every case. `en-GB`'s
 * `month: "short"` is four for September — `Sept` — which is correct English and is not what the
 * design writes, and a board writing `1 Sept 2026` beside a design writing `1 Sep 2026` is a
 * difference in copy rather than in data.
 *
 * So the abbreviation comes from `Intl` and is cut to three characters, rather than from a
 * hand-written table of twelve strings that would be a second opinion about a locale. September is
 * the only month it changes.
 */
const monthName = new Intl.DateTimeFormat("en-GB", { month: "short", timeZone: "UTC" });

function shortMonth(at: Date): string {
  return monthName.format(at).slice(0, 3);
}

/**
 * An ISO instant as the design writes a date: `1 Sep 2026`.
 *
 * UTC, because every instant on these boards is published as UTC and the design names no zone
 * anywhere; Q-24 records what a real tenant's zone costs. The day is not padded — the design writes
 * `1 Sep 2026`, not `01`.
 */
export function asDay(instant: string): string {
  const parsed = Date.parse(instant);
  // An unparseable instant is shown as published rather than as "Invalid Date". Every one of these
  // contracts types the field as `date-time`, so this is a row the platform wrote wrong.
  if (!Number.isFinite(parsed)) return instant;
  const at = new Date(parsed);
  return `${String(at.getUTCDate())} ${shortMonth(at)} ${String(at.getUTCFullYear())}`;
}

/** The design's `Mar 2024` grammar, for a column whose question is which month rather than which day. */
export function asMonth(instant: string): string {
  const parsed = Date.parse(instant);
  if (!Number.isFinite(parsed)) return instant;
  const at = new Date(parsed);
  return `${shortMonth(at)} ${String(at.getUTCFullYear())}`;
}

/** Which side of its effective window an instant falls. The zones board's rule, on four services. */
export type WindowPosition = "inside" | "not_yet" | "ended";

export function windowPosition(
  effectiveFrom: string,
  effectiveUntil: string | null | undefined,
  now: number,
): WindowPosition {
  const from = Date.parse(effectiveFrom);
  if (Number.isFinite(from) && from > now) return "not_yet";
  if (effectiveUntil !== undefined && effectiveUntil !== null) {
    const until = Date.parse(effectiveUntil);
    if (Number.isFinite(until) && until <= now) return "ended";
  }
  return "inside";
}

/**
 * How many rows to take in one request.
 *
 * All four listings publish the same `limit`, word for word: "Rows per page. Fifty when absent, two
 * hundred at most", `minimum: 1`, `maximum: 200`. The maximum is a schema bound rather than a
 * clamp, so 201 is a 400 rather than a page of 200 — which is why the number is written once here
 * instead of per screen. One request is the whole board for every tenant the design draws, and when
 * it is not, `boundedNote` says so.
 */
export const PER_FETCH = "200";
